import React, { FC, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useQuery, gql } from "@apollo/client"
import DOMPurify from "dompurify"

// Selectors
import { selectPrimaryActionColor } from "Containers/BusinessInformations/selectors"

// Styles
import {
  ActualityContainer,
  FeedSourceLink,
  SearchWrapper,
  SearchInput,
  MainArea,
  CardsList,
  ArticleCard,
  CardHeader,
  CardTitle,
  CardMeta,
  MetaItem,
  CardSnippet,
  CategoriesRow,
  CategoryTag,
  CardFooter,
  FooterText,
  Arrow,
  SkeletonList,
  SkeletonCard,
  SkeletonLine,
  EmptyState,
  EmptyTitle,
  EmptyText,
} from "./actualityRSS.style"

const GET_RSS_FEED = gql`
  query GetRssFeed($url: String!, $limit: Float) {
    getRssFeed(url: $url, limit: $limit) {
      items {
        title
        pubDate
        link
        contentSnippet
        content
        categories
        author
      }
      description
      link
      title
    }
  }
`

const DENSITY_PRESETS = [
  { grid: 3, label: "Normal", width: "400px" },
  { grid: 4, label: "Dense", width: "365px" },
  { grid: 5, label: "Ultra-dense", width: "265px" },
] as const

interface IActualityRSSProps {
  rssUrl?: string
}

const ActualityRSS: FC<IActualityRSSProps> = ({ rssUrl }) => {
  /** Selectors */
  const primaryColor: string = useSelector(selectPrimaryActionColor)

  /** State */
  const [density, setDensity] = useState<number>(3)
  const [search, setSearch] = useState<string>("")

  /** Apollo */
  const { loading, data, error } = useQuery(GET_RSS_FEED, {
    variables: { limit: 30, url: rssUrl },
    skip: !rssUrl,
  })

  const feed = data?.getRssFeed
  const items = feed?.items || []

  /** Filtrage simple par titre + contenu */
  const filteredItems = useMemo(() => {
    if (!search.trim()) return items
    const term = search.toLowerCase()
    return items.filter((item: any) => {
      const inTitle = item.title?.toLowerCase().includes(term)
      const inSnippet = item.contentSnippet?.toLowerCase().includes(term)
      return inTitle || inSnippet
    })
  }, [items, search])

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return null
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  const densityLabel = DENSITY_PRESETS?.find(preset => preset.grid === density)?.label

  return (
    <ActualityContainer id="actuality-rss" primaryColor={primaryColor} density={density}>
      <section className="content_wrapper">
        <header className="actuality_header">
          <div className="actuality_header_left">
            <h1 className="feed_title">{feed?.title || "Actualités"}</h1>
            {feed?.description && <p className="feed_subtitle">{feed.description}</p>}
            {feed?.link && (
              <FeedSourceLink href={feed.link} target="_blank" rel="noreferrer">
                Voir la source
              </FeedSourceLink>
            )}
          </div>
          <div className="actuality_header_right">
            <div className="actuality_header_right_button_wrap">
              <button
                onClick={() =>
                  setDensity(prev => {
                    const preset = DENSITY_PRESETS?.find(preset => preset.grid === prev + 1)
                    const newDensity = prev + 1 > 5 ? DENSITY_PRESETS[0]?.grid : preset?.grid
                    return newDensity || 3
                  })
                }
                aria-pressed={density > 3}
                className="button_item"
                title={`Densité : ${densityLabel}`}
              >
                <i
                  className={
                    density === 5
                      ? "fas fa-th"
                      : density === 3
                      ? "fas fa-th-large"
                      : "fas fa-grip-horizontal"
                  }
                />
              </button>
            </div>
            <SearchWrapper>
              <SearchInput
                type="text"
                placeholder="Rechercher un article..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </SearchWrapper>
          </div>
        </header>

        <MainArea>
          {loading && (
            <SkeletonList density={density}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  style={{
                    gridTemplateColumns: `repeat(${density || 3}, 1fr)`,
                    gridGap: "20px",
                    display: "grid",
                  }}
                >
                  <SkeletonCard key={i}>
                    <SkeletonLine width="70%" />
                    <SkeletonLine width="40%" />
                    <SkeletonLine width="90%" />
                  </SkeletonCard>
                  <SkeletonCard key={i}>
                    <SkeletonLine width="70%" />
                    <SkeletonLine width="40%" />
                    <SkeletonLine width="90%" />
                  </SkeletonCard>
                  <SkeletonCard key={i}>
                    <SkeletonLine width="70%" />
                    <SkeletonLine width="40%" />
                    <SkeletonLine width="90%" />
                  </SkeletonCard>
                </div>
              ))}
            </SkeletonList>
          )}

          {error && !loading && (
            <EmptyState>
              <EmptyTitle>Oups…</EmptyTitle>
              <EmptyText>
                Impossible de charger le flux RSS pour le moment.
                <br />
                <small>{error.message}</small>
              </EmptyText>
            </EmptyState>
          )}

          {!loading && !error && filteredItems.length === 0 && (
            <EmptyState>
              <EmptyTitle>Aucun résultat</EmptyTitle>
              <EmptyText>
                Aucun article ne correspond à votre recherche.
                <br />
                Modifiez les filtres ou réessayez plus tard.
              </EmptyText>
            </EmptyState>
          )}

          {!loading && !error && filteredItems.length > 0 && (
            <CardsList density={density}>
              {filteredItems.map((item: any) => {
                const dateLabel = formatDate(item.pubDate)
                const snippet =
                  item.contentSnippet || item.content || "Aucun résumé disponible pour cet article."

                return (
                  <ArticleCard
                    key={item.link}
                    href={item.link}
                    target="_blank"
                    rel="noreferrer"
                    primaryColor={primaryColor}
                  >
                    <CardHeader>
                      <CardTitle>{item.title}</CardTitle>
                    </CardHeader>

                    <CardMeta>
                      {dateLabel && <MetaItem>{dateLabel}</MetaItem>}
                      {item.author && <MetaItem>Par {item.author}</MetaItem>}
                    </CardMeta>

                    {snippet?.includes("<p>") ? (
                      <CardSnippet
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(snippet) }}
                      />
                    ) : (
                      <CardSnippet>{snippet}</CardSnippet>
                    )}

                    {item.categories && item.categories.length > 0 && (
                      <CategoriesRow>
                        {item.categories.map((cat: string) => (
                          <CategoryTag key={cat} $primaryColor={primaryColor}>
                            {cat}
                          </CategoryTag>
                        ))}
                      </CategoriesRow>
                    )}

                    <CardFooter>
                      <FooterText>Lire l'article</FooterText>
                      <Arrow>↗</Arrow>
                    </CardFooter>
                  </ArticleCard>
                )
              })}
            </CardsList>
          )}
        </MainArea>
      </section>
    </ActualityContainer>
  )
}

export default ActualityRSS
